import {
  ensureJourneyDb,
  getById,
  insertRow,
  putBlob,
  rowsFromIndex,
  updateRow,
  writeAudit,
} from "../../db";
import { createArchiveRecord, createExport, nowIso } from "../../db/schema";
import {
  assertTransactionAccess,
  canArchiveTransaction,
  canExportTransaction,
  exportTypeForActor,
  resolveActor,
} from "./access";
import { forbidden, notFound } from "./errors";
import { checklistSnapshot, hydrateTransaction, notifyUser } from "./helpers";
import { describeEmailFlags, queueArchiveEmails, readEmailFlags } from "./emailOutbox";
import { buildReportModel } from "../../export/reportModel";
import { renderReportHtml } from "../../export/reportHtml";

function collectExportPayload(transaction) {
  const hydrated = hydrateTransaction(transaction);
  const payload = {
    format: "html",
    generated_at: nowIso(),
    transaction: {
      id: transaction.id,
      property_address: transaction.property_address,
      status: transaction.status,
      created_at: transaction.created_at,
      closed_at: transaction.closed_at,
      client_role: transaction.client_role,
    },
    parties: {
      broker: hydrated.broker,
      agent: hydrated.agent,
      client: hydrated.client,
    },
    threads: hydrated.threads,
    checklist: checklistSnapshot(transaction),
    attachments: hydrated.threads.flatMap((thread) =>
      thread.messages.flatMap((message) =>
        (message.attachments || []).map((item) => ({
          ...item,
          thread_id: thread.id,
          message_id: message.id,
        }))
      )
    ),
  };
  return payload;
}

function emailPlan(transaction) {
  const client = getById("users", transaction.client_id);
  return {
    queued: true,
    broker: true,
    agent: true,
    client: Boolean(client?.email_report_opt_in),
    note: "Flags are queued locally. A mail server is not attached in this build.",
  };
}

function notifyExportRecipients(transaction, exportRow, actor) {
  const client = getById("users", transaction.client_id);
  const recipients = [transaction.broker_id, transaction.agent_id];
  if (client?.email_report_opt_in) recipients.push(transaction.client_id);
  [...new Set(recipients.filter(Boolean))].forEach((userId) => {
    notifyUser({
      user_id: userId,
      type: "export",
      actor_id: actor.userId,
      transaction_id: transaction.id,
      payload: {
        export_id: exportRow.id,
        transaction_id: transaction.id,
        export_type: exportRow.export_type,
      },
    });
  });
}

/** POST /api/transactions/:id/export */
export function postTransactionExport(transactionId, session, options = {}) {
  ensureJourneyDb();
  const actor = resolveActor(session);
  const transaction = getById("transactions", transactionId);
  if (!transaction) throw notFound("Transaction not found");
  if (!canExportTransaction(actor, transaction)) {
    throw forbidden("Only the assigned agent or supervising broker can export");
  }

  const exportType = options.export_type || exportTypeForActor(actor);
  const payload = collectExportPayload(transaction);
  payload.format = "html";
  payload.export_type = exportType;
  const model = buildReportModel(payload, {
    exportType,
    generatedBy: actor.name,
  });
  const html = renderReportHtml(model);

  const record = insertRow(
    "exports",
    createExport({
      transaction_id: transaction.id,
      generated_by_user_id: actor.userId,
      export_type: exportType,
      file_path: "",
    })
  );
  putBlob(`local://exports/${record.id}.json`, payload);
  const filePath = putBlob(`local://exports/${record.id}.html`, html);
  const saved = updateRow("exports", record.id, { file_path: filePath });

  writeAudit({
    user_id: actor.userId,
    action_type: "export.generate",
    transaction_id: transaction.id,
    metadata: { export_id: saved.id, export_type: exportType, formats: ["json", "html", "pdf"] },
  });

  if (options.notify !== false) {
    notifyExportRecipients(transaction, saved, actor);
  }

  return {
    ...saved,
    email: emailPlan(transaction),
    payload,
    html,
  };
}

/** POST /api/transactions/:id/archive */
export function postTransactionArchive(transactionId, session, body = {}) {
  ensureJourneyDb();
  const actor = resolveActor(session);
  const transaction = getById("transactions", transactionId);
  if (!transaction) throw notFound("Transaction not found");
  if (!canArchiveTransaction(actor, transaction)) {
    throw forbidden("Only the assigned agent or supervising broker can archive");
  }

  const archiveType = body.status === "abandoned" ? "abandoned" : "closed";
  const existing = rowsFromIndex("archive_records", "archivesByTransactionId", transaction.id);
  if (existing.length && (transaction.status === "closed" || transaction.status === "abandoned")) {
    const latestExport = rowsFromIndex("exports", "exportsByTransactionId", transaction.id).at(-1) || null;
    const email = readEmailFlags(existing[0].id);
    writeAudit({
      user_id: actor.userId,
      action_type: "transaction.archive",
      transaction_id: transaction.id,
      metadata: { idempotent: true, archive_id: existing[0].id },
    });
    return {
      transaction: hydrateTransaction(transaction),
      archive: existing[0],
      export: latestExport,
      email,
      idempotent: true,
    };
  }

  const exported = postTransactionExport(transactionId, session, {
    export_type: "broker",
    notify: false,
  });
  const next = updateRow("transactions", transaction.id, {
    status: archiveType,
    closed_at: nowIso(),
  });
  rowsFromIndex("message_threads", "threadsByTransactionId", transaction.id).forEach((thread) => {
    if (thread.status !== "archived") {
      updateRow("message_threads", thread.id, { status: "archived" });
    }
  });
  const archive = insertRow(
    "archive_records",
    createArchiveRecord({
      transaction_id: transaction.id,
      archive_type: archiveType,
      report_export_id: exported.id,
    })
  );
  const email = queueArchiveEmails({
    transaction: next,
    archive,
    exportRow: exported,
    actor,
  });

  writeAudit({
    user_id: actor.userId,
    action_type: "transaction.status_change",
    transaction_id: transaction.id,
    metadata: { from: transaction.status, to: archiveType },
  });
  writeAudit({
    user_id: actor.userId,
    action_type: "transaction.archive",
    transaction_id: transaction.id,
    metadata: { archive_id: archive.id, export_id: exported.id, email: describeEmailFlags(email) },
  });

  const archiveNotice = {
    archive_id: archive.id,
    archive_type: archiveType,
    export_id: exported.id,
  };
  notifyUser({
    user_id: transaction.broker_id,
    type: "archive",
    actor_id: actor.userId,
    transaction_id: transaction.id,
    payload: archiveNotice,
  });
  if (transaction.agent_id && transaction.agent_id !== transaction.broker_id) {
    notifyUser({
      user_id: transaction.agent_id,
      type: "archive",
      actor_id: actor.userId,
      transaction_id: transaction.id,
      payload: archiveNotice,
    });
  }
  if (email.client?.send && transaction.client_id) {
    notifyUser({
      user_id: transaction.client_id,
      type: "archive",
      actor_id: actor.userId,
      transaction_id: transaction.id,
      payload: archiveNotice,
    });
  }

  return {
    transaction: hydrateTransaction(next),
    archive,
    export: exported,
    email,
    idempotent: false,
  };
}

export function getTransaction(transactionId, session) {
  ensureJourneyDb();
  const actor = resolveActor(session);
  const transaction = getById("transactions", transactionId);
  if (!transaction) throw notFound("Transaction not found");
  assertTransactionAccess(actor, transaction);
  return hydrateTransaction(transaction);
}
